import { defineStore } from 'pinia';
import { CommunityType, LegalEntityType, CommunityIdentificationType } from '../../types/src/enums';
import type { IEnergyCommunity, ICommunityContact } from '../../types/src/schemas';
import axios from 'axios';

// Create axios instance with base URL
let backendBaseUrl = import.meta.env.VITE_BACKEND_BASEURL;
let apiBaseUrl = import.meta.env.VITE_API_BASE_URL;

// Normalize URLs to ensure we don't have double slashes
if (backendBaseUrl && backendBaseUrl.endsWith('/')) {
  backendBaseUrl = backendBaseUrl.slice(0, -1);
}
if (apiBaseUrl && apiBaseUrl.endsWith('/')) {
  apiBaseUrl = apiBaseUrl.slice(0, -1);
}

const baseURL = backendBaseUrl || apiBaseUrl || '/api';

const api = axios.create({
  baseURL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add request interceptor for debugging
api.interceptors.request.use(request => {
  return request;
});

/**
 * Form-compatible version of IAddressPostal for backward compatibility
 */
interface CommunityAddressPostal {
  name: string;
  address: string;
  addressExtra: string;
  zip: string;
  city: string;
}

/**
 * Type for community data from the API
 */
export interface FormEnergyCommunity {
  ecn: string;                                   // Energy community number
  ecid: string;                                  // Energy community ID
  name: string;                                  // Community name
  communityType: CommunityType | string;         // Type of community per EAG

  dso: string;                                   // Distribution system operator
  dsoLinkCheck: string;                          // DSO portal link
  linkCustomerPortal?: string;                   // URL to DSO customer portal

  legal: LegalEntityType | string;               // Legal entity type
  identificationType: CommunityIdentificationType | string;
  identification: string;                        // ID number (ZVR, FB, etc.)

  logoSrc: string;                               // URL to community logo
  uriMoreInfo: string;                           // URL to community website

  transformerReference: string;                  // Transformer station ID
  substationReference: string;                   // Substation ID

  addressPostal: CommunityAddressPostal;         // Form-compatible postal address
  contact: ICommunityContact;                    // Contact information
  contacts?: ICommunityContact[];               // Additional contacts
  allow0MeteringPoints: boolean;
  communityNotes?: string;                      // Optional notes/hints for the community
}

interface CommunityApiState {
  loading: boolean;
  error: string | null;
}

export const useCommunityApiStore = defineStore('communityApiStore', {
  state: (): CommunityApiState => ({
    loading: false,
    error: null
  }),

  actions: {
    /**
     * Fetch all communities from the API
     */
    async fetchCommunities(): Promise<FormEnergyCommunity[]> {
      this.loading = true;
      this.error = null;

      try {
        const response = await api.get('/communityPublicInformation');

        // Check if response data is valid JSON
        if (typeof response.data === 'object' && Array.isArray(response.data)) {
          return response.data;
        } else {
          throw new Error('Invalid API response format');
        }
      } catch (error) {
        this.error = error instanceof Error ? error.message : 'Failed to fetch communities';
        return [];
      } finally {
        this.loading = false;
      }
    },

    /**
     * Fetch a specific community by ECN
     */
    async fetchCommunityByEcn(ecn: string): Promise<FormEnergyCommunity | null> {
      this.loading = true;
      this.error = null;

      try {
        const response = await api.get(`/communityPublicInformation/${ecn}`);

        // Check if response data is valid JSON and has required fields
        if (typeof response.data === 'object' && response.data !== null && 'ecn' in response.data) {
          return response.data;
        } else {
          throw new Error('Invalid API response format');
        }
      } catch (error) {
        this.error = error instanceof Error ? error.message : `Failed to fetch community with ECN ${ecn}`;
        return null;
      } finally {
        this.loading = false;
      }
    }
  }
});
